import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Shield, Users, MessageCircle, Sparkles, CheckCircle2, Clock } from "lucide-react";

const initialForm = { name: "", email: "", phone: "", city: "", experience: "", motivation: "", availability: "", ref_links: "" };

export default function RecruitModerator() {
  const { user } = useAuth();
  const [form, setForm] = useState(() => ({
    ...initialForm,
    name: user?.name || "",
    email: user?.email || "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit = form.name.trim() && form.email.trim() && form.motivation.trim().length >= 20 && form.experience.trim().length >= 20;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Mohon lengkapi nama, email, motivasi & pengalaman (min 20 karakter)"); return; }
    try {
      setSubmitting(true);
      const res = await api.post("/moderator-applications", form);
      if (res.data?.already) toast.info("Anda sudah punya aplikasi yang menunggu ditinjau");
      else toast.success("Aplikasi terkirim — tim editorial akan meninjau dalam beberapa hari");
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim aplikasi");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-16" data-testid="mod-application-success">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] flex items-center justify-center mb-5">
            <CheckCircle2 size={32} className="text-[#1E3A8A]" />
          </div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">TERKIRIM</div>
          <h1 className="text-4xl sm:text-5xl font-serif text-[#1F2937] mb-3">Terima kasih, {form.name.split(" ")[0]}</h1>
          <p className="text-base text-[#4B5563] max-w-xl mx-auto mb-8">
            Kami membaca setiap aplikasi. Tim editorial akan meninjau berkas Anda dalam <strong>3–7 hari kerja</strong>. Bila cocok, kami akan menghubungi lewat email di <strong>{form.email}</strong>.
          </p>
          <Link to="/" className="inline-block px-6 py-2.5 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm">Kembali ke beranda</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="recruit-moderator-page">
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-3 inline-flex items-center gap-2">
        <Shield size={13} className="text-[#1E3A8A]" /> RELAWAN MODERATOR
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#1F2937] leading-[1.05] max-w-3xl">
        Bantu kami jaga percakapan orang tua tetap <em className="italic">sehat & jujur</em>.
      </h1>
      <p className="mt-5 text-base text-[#4B5563] max-w-2xl">
        The School Reports mengandalkan orang tua sukarelawan untuk memoderasi forum, menyaring review, dan menjaga foto sekolah tetap layak. Anda akan bekerja bersama tim editorial dengan panduan yang jelas dan dukungan penuh.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-[#EAE7E0] bg-white p-5">
          <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center mb-3"><MessageCircle size={16} className="text-[#1E3A8A]" /></div>
          <div className="text-sm font-semibold text-[#1F2937]">Moderasi Forum</div>
          <div className="text-xs text-[#4B5563] mt-1.5">Awasi thread & balasan, pin diskusi bernilai, hapus konten yang melanggar pedoman.</div>
        </div>
        <div className="rounded-2xl border border-[#EAE7E0] bg-white p-5">
          <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center mb-3"><Users size={16} className="text-[#1E3A8A]" /></div>
          <div className="text-sm font-semibold text-[#1F2937]">Tinjau Review</div>
          <div className="text-xs text-[#4B5563] mt-1.5">Loloskan review yang lengkap, kembalikan yang perlu revisi. Semua sesuai SOP editorial.</div>
        </div>
        <div className="rounded-2xl border border-[#EAE7E0] bg-white p-5">
          <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center mb-3"><Sparkles size={16} className="text-[#1E3A8A]" /></div>
          <div className="text-sm font-semibold text-[#1F2937]">Kelola Galeri Foto</div>
          <div className="text-xs text-[#4B5563] mt-1.5">Tindaklanjuti laporan foto, jaga privasi anak, dan pin foto yang paling merepresentasikan sekolah.</div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-2 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">Yang kami cari</div>
            <ul className="text-sm text-[#1F2937] space-y-2">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" /> Orang tua aktif di komunitas sekolah</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" /> Netral, obyektif, tidak berafiliasi ke sekolah tertentu</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" /> Sanggup meluangkan minimal 3 jam/minggu</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" /> Punya akun di The School Reports</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-[#F3F1ED] p-5 border border-[#EAE7E0]">
            <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1 inline-flex items-center gap-1.5"><Clock size={12} /> Waktu proses</div>
            <div className="text-sm text-[#1F2937]">Tim editorial akan meninjau aplikasi dalam 3–7 hari kerja. Jika cocok, kami hubungi via email untuk wawancara singkat sebelum akses moderasi diaktifkan.</div>
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-3 space-y-4 bg-white rounded-3xl border border-[#EAE7E0] p-6 lg:p-8" data-testid="mod-application-form">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Nama lengkap</div>
            <Input value={form.name} onChange={set("name")} data-testid="mod-app-name" placeholder="Nama sesuai KTP" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Email</div>
              <Input type="email" value={form.email} onChange={set("email")} data-testid="mod-app-email" placeholder="nama@email.com" required />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">No. WhatsApp (opsional)</div>
              <Input value={form.phone} onChange={set("phone")} data-testid="mod-app-phone" placeholder="+62…" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Kota</div>
              <Input value={form.city} onChange={set("city")} data-testid="mod-app-city" placeholder="Jakarta Selatan" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Waktu tersedia</div>
              <Input value={form.availability} onChange={set("availability")} data-testid="mod-app-availability" placeholder="mis: 5 jam/minggu, malam hari" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Pengalaman relevan <span className="text-[#4B5563]/70">(min 20 karakter)</span></div>
            <Textarea rows={4} value={form.experience} onChange={set("experience")} data-testid="mod-app-experience"
              placeholder="mis: aktif di komunitas orang tua sekolah anak, pernah menjadi admin grup WA orang tua, pengalaman menulis review sekolah…" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Kenapa Anda ingin bergabung? <span className="text-[#4B5563]/70">(min 20 karakter)</span></div>
            <Textarea rows={5} value={form.motivation} onChange={set("motivation")} data-testid="mod-app-motivation"
              placeholder="Ceritakan singkat motivasi Anda, dan bagaimana Anda menjaga netralitas saat ada review negatif tentang sekolah anak sendiri." />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Tautan referensi (opsional)</div>
            <Input value={form.ref_links} onChange={set("ref_links")} data-testid="mod-app-refs" placeholder="LinkedIn / IG / blog pribadi (pisah koma)" />
          </div>
          <div className="pt-2 flex items-center justify-between">
            <div className="text-[11px] text-[#4B5563]">Dengan mengirim, Anda setuju dihubungi tim editorial via email.</div>
            <Button type="submit" disabled={submitting || !canSubmit} data-testid="mod-app-submit"
              className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11 px-6 disabled:opacity-50">
              {submitting ? "Mengirim…" : "Kirim aplikasi"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
